const SITE_NAME = 'Jobifull'
const SITE_ORIGIN = 'https://jobifull.eu'
const DEFAULT_IMAGE = `${SITE_ORIGIN}/img/jobifull-retangular.png`
const DEFAULT_TITLE = 'Jobifull — Empregos internacionais e talentos sem fronteiras'
const DEFAULT_DESCRIPTION = 'Encontre vagas internacionais, conecte-se com empresas e desenvolva sua carreira sem fronteiras com a Jobifull.'

function cleanText(value, maxLength = 160) {
  const text = String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 1).trimEnd()}…`
}

function absoluteUrl(value = '/') {
  try {
    return new URL(value, `${SITE_ORIGIN}/`).toString()
  } catch (_) {
    return `${SITE_ORIGIN}/`
  }
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value))
}

function upsertCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', url)
}

function removeJobStructuredData() {
  document.head.querySelector('script[data-jobifull-job-posting]')?.remove()
}

function upsertJobStructuredData(job, canonicalUrl) {
  removeJobStructuredData()
  const expiration = Date.parse(job?.expires_at)
  if (job?.is_closed === true || (job?.job_status && job.job_status !== 'published') || (!Number.isNaN(expiration) && expiration <= Date.now())) return
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.dataset.jobifullJobPosting = 'true'

  const key = value => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  const employmentType = {
    full_time: 'FULL_TIME',
    part_time: 'PART_TIME',
    contract: 'CONTRACTOR',
    internship: 'INTERN',
    temporary: 'TEMPORARY',
    fulltime: 'FULL_TIME', parttime: 'PART_TIME', contractor: 'CONTRACTOR', intern: 'INTERN',
  }[key(job?.employment_type || job?.employment_type_key || job?.job_period)]
  const location = job?.location || {}
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: cleanText(job?.job_title || job?.title, 100),
    description: cleanText(job?.job_description || job?.description, 5000),
    datePosted: job?.published_at || job?.created_at,
    validThrough: job?.expires_at || undefined,
    employmentType,
    url: canonicalUrl,
    hiringOrganization: {
      '@type': 'Organization',
      name: cleanText(job?.company_name || job?.company?.name, 100),
      sameAs: job?.company_slug ? absoluteUrl(`/companies/${job.company_slug}`) : undefined,
      logo: job?.company_logo ? absoluteUrl(job.company_logo) : undefined,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job?.job_city || location?.city || undefined,
        addressRegion: job?.job_state || location?.state || undefined,
        addressCountry: /^[A-Z]{2}$/.test(String(job?.job_country_code || location?.country_code || '').toUpperCase())
          ? String(job?.job_country_code || location?.country_code).toUpperCase() : undefined,
      },
    },
  }
  const minimum = Number(job?.minimum_wage ?? job?.salary_range?.minimum_minor / 100)
  const maximum = Number(job?.max_wage ?? job?.salary_range?.maximum_minor / 100)
  const currency = String(job?.wage_currency_code || job?.salary_range?.currency_code || '').toUpperCase()
  const unitText = {
    hour: 'HOUR', hourly: 'HOUR', per_hour: 'HOUR', day: 'DAY', daily: 'DAY', per_day: 'DAY',
    week: 'WEEK', weekly: 'WEEK', per_week: 'WEEK', month: 'MONTH', monthly: 'MONTH', per_month: 'MONTH',
    year: 'YEAR', yearly: 'YEAR', annually: 'YEAR', annual: 'YEAR', per_year: 'YEAR',
  }[key(job?.payment_period_key || job?.salary_range?.period)]
  if ((minimum > 0 || maximum > 0) && /^[A-Z]{3}$/.test(currency) && unitText) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency,
      value: {
        '@type': 'QuantitativeValue',
        minValue: minimum || undefined,
        maxValue: maximum || undefined,
        unitText,
      },
    }
  }
  script.textContent = JSON.stringify(schema, (_, value) => value === undefined ? undefined : value)
  document.head.appendChild(script)
}

export function setSeoMeta({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  type = 'website',
  image = DEFAULT_IMAGE,
} = {}) {
  if (typeof document === 'undefined') return

  const safeTitle = cleanText(title, 70) || DEFAULT_TITLE
  const safeDescription = cleanText(description) || DEFAULT_DESCRIPTION
  const canonicalUrl = absoluteUrl(path)
  const shareImage = absoluteUrl(image || DEFAULT_IMAGE)

  document.title = safeTitle
  upsertCanonical(canonicalUrl)
  upsertMeta('meta[name="description"]', { name: 'description', content: safeDescription })
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME })
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'pt_BR' })
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type })
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: safeTitle })
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: safeDescription })
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: shareImage })
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: safeTitle })
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: safeDescription })
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: shareImage })
}

export function applyRouteSeo(route) {
  const seo = route?.meta?.seo || {}
  if (typeof document !== 'undefined') removeJobStructuredData()
  setSeoMeta({ ...seo, path: route?.path || '/' })
}

export function setJobSeo(job, path) {
  const title = `${cleanText(job?.job_title, 45) || 'Vaga de emprego'} | ${cleanText(job?.company_name, 20) || SITE_NAME}`
  const location = cleanText(job?.full_location || job?.location, 45)
  const fallback = `Confira esta oportunidade${location ? ` em ${location}` : ''} e candidate-se pela Jobifull.`
  setSeoMeta({
    title,
    description: cleanText(job?.job_description) || fallback,
    path,
    type: 'article',
  })
  if (typeof document !== 'undefined') upsertJobStructuredData(job, absoluteUrl(path))
}

export function setCompanySeo(company, path) {
  const name = cleanText(company?.company_name, 50) || 'Empresa'
  setSeoMeta({
    title: `${name} — Perfil da empresa | ${SITE_NAME}`,
    description: cleanText(company?.company_description) || `Conheça ${name}, suas oportunidades e seu perfil na Jobifull.`,
    path,
    type: 'profile',
  })
}

export const seoDefaults = {
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  image: DEFAULT_IMAGE,
}

